import { useCallback, useEffect } from "react";
import { useSearchParams } from "react-router";
import { useQuery } from "@apollo/client/react";
import { useStore } from "@/app/store";
import {
  GET_LINES,
  GET_OPERATIONAL_DATA,
  GET_OPERATIONAL_DATA_YEARS,
} from "../graphql/queries";
import { operationalDataKey } from "../store/operationalDataSlice";
import { resolveYear } from "./resolveYear";

const parseYearParam = (raw: string | null): number | null => {
  if (raw === null) return null;
  const parsed = Number(raw);
  return Number.isInteger(parsed) ? parsed : null;
};

const useOperationalData = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedLine = searchParams.get("line");
  const urlYear = parseYearParam(searchParams.get("year"));

  const setOperationalData = useStore((s) => s.setOperationalData);
  const clearOperationalData = useStore((s) => s.clearOperationalData);
  const operationalData = useStore((s) => s.operationalData);

  const {
    data: linesData,
    loading: linesLoading,
    error: linesError,
    refetch: refetchLines,
  } = useQuery(GET_LINES);

  const {
    data: yearsData,
    loading: yearsLoading,
    error: yearsError,
    refetch: refetchYears,
  } = useQuery(GET_OPERATIONAL_DATA_YEARS, {
    variables: { line: selectedLine ?? "" },
    skip: selectedLine === null,
  });

  const years = yearsData?.operationalDataYears ?? [];
  const resolvedYear = resolveYear(urlYear, years);

  // Reconcile the URL year against the line's available years. Only rewrite when
  // the resolved year differs from the URL, so a valid deeplink is left untouched.
  useEffect(() => {
    if (selectedLine === null) return;
    if (yearsLoading || years.length === 0) return;
    if (resolvedYear !== null && resolvedYear !== urlYear) {
      setSearchParams(
        { line: selectedLine, year: String(resolvedYear) },
        { replace: true },
      );
    }
  }, [
    selectedLine,
    urlYear,
    resolvedYear,
    years.length,
    yearsLoading,
    setSearchParams,
  ]);

  const {
    data: gridData,
    loading: gridLoading,
    error: gridError,
    refetch: refetchGrid,
  } = useQuery(GET_OPERATIONAL_DATA, {
    variables: { line: selectedLine ?? "", year: resolvedYear ?? 0 },
    skip: selectedLine === null || resolvedYear === null,
  });

  useEffect(() => {
    // No complete (line, year) selection — e.g. the nav item was re-clicked,
    // dropping the query params. Clear the slice so a previous selection's grid
    // doesn't linger under the now-empty pickers.
    if (selectedLine === null || resolvedYear === null) {
      clearOperationalData();
      return;
    }
    if (gridLoading || gridData === undefined) return;
    setOperationalData(
      gridData.operationalData ?? null,
      operationalDataKey(selectedLine, resolvedYear),
    );
  }, [
    gridData,
    gridLoading,
    selectedLine,
    resolvedYear,
    setOperationalData,
    clearOperationalData,
  ]);

  const selectLine = useCallback(
    (line: string) => {
      // Defer year resolution to the reconciliation effect once the new line's
      // years load; keep the current year in the URL so it's preserved when the
      // new line still offers it.
      const next: Record<string, string> = { line };
      if (urlYear !== null) next.year = String(urlYear);
      setSearchParams(next, { replace: true });
    },
    [urlYear, setSearchParams],
  );

  const selectYear = useCallback(
    (year: number) => {
      if (selectedLine === null) return;
      setSearchParams(
        { line: selectedLine, year: String(year) },
        { replace: true },
      );
    },
    [selectedLine, setSearchParams],
  );

  const retry = useCallback(() => {
    refetchLines();
    if (selectedLine !== null) refetchYears();
    if (selectedLine !== null && resolvedYear !== null) refetchGrid();
  }, [refetchLines, refetchYears, refetchGrid, selectedLine, resolvedYear]);

  return {
    lines: linesData?.lines ?? [],
    selectedLine,
    years,
    selectedYear: resolvedYear,
    operationalData,
    loading: linesLoading || yearsLoading || gridLoading,
    error: linesError ?? yearsError ?? gridError,
    onLineChange: selectLine,
    onYearChange: selectYear,
    onRetry: retry,
  };
};

export default useOperationalData;
